import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Link,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { filledUploadDropzoneSx } from "./listNewAssetFilledSurface";

const MAX_GALLERY_FILES = 6;
const MAX_FILE_MB = 10;

export default function ListNewAssetStep3({
  draft,
  onDraftChange,
  nextStep,
  prevStep,
  wizardStep = 3,
  wizardTotalSteps = 8,
  categoryChipLabel = "Real Estate",
  isEditMode = false,
}) {
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;
  const [errors, setErrors] = useState({});
  const mainImage = draft.mainImage;
  const galleryImages = draft.galleryImages || [];
  const mainInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleMainUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, mainImage: `File must be ${MAX_FILE_MB}MB or smaller.` }));
      e.target.value = "";
      return;
    }
    onDraftChange({ mainImage: file });
    setErrors((p) => ({ ...p, mainImage: "" }));
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized) {
      setErrors((p) => ({ ...p, galleryImages: `"${oversized.name}" exceeds ${MAX_FILE_MB}MB. Please select smaller files.` }));
      e.target.value = "";
      return;
    }
    const merged = [...galleryImages, ...files].slice(0, MAX_GALLERY_FILES);
    onDraftChange({ galleryImages: merged });
    setErrors((p) => ({ ...p, galleryImages: "" }));
  };

  const hasMainImage =
    mainImage || (isEditMode && Boolean(draft._editMeta?.mainImageUrl));

  const handleNext = () => {
    if (!hasMainImage) {
      setErrors({ mainImage: "Please upload a main display image." });
      return;
    }
    setErrors({});
    nextStep();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1100,
        mx: "auto",
        minHeight: "100vh",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={prevStep}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: TEAL,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: TEAL_HOVER },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={categoryChipLabel}
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              color: (theme) => theme.palette.brand.cyanDark,
              fontWeight: 600,
              fontSize: 12,
              height: 26,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
            Step {wizardStep} of {wizardTotalSteps}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: wizardTotalSteps }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i < wizardStep ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
        >
          Asset Images
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 2.5 }}>
          Upload high-quality images
        </Typography>

        <Typography
          sx={{ fontWeight: 600, fontSize: 13, color: "text.primary", mb: 1 }}
        >
          Main Display Image{" "}
          <Box component="span" sx={{ color: "error.main" }}>
            *
          </Box>
        </Typography>
        <input
          ref={mainInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleMainUpload}
          hidden
        />
        <Paper
          component="button"
          type="button"
          onClick={() => mainInputRef.current?.click()}
          elevation={0}
          sx={{
            width: "100%",
            minHeight: 140,
            borderRadius: 2,
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 2,
            cursor: "pointer",
            "&:hover": {
              borderColor: (theme) => theme.palette.brand.cyan,
              bgcolor: "background.subtle",
            },
            ...filledUploadDropzoneSx(
              theme,
              Boolean(mainImage || draft._editMeta?.mainImageUrl),
              theme.palette.accent.teal.dashedBorder
            ),
          }}
        >
          <Stack alignItems="center" spacing={0.5}>
            <ImageOutlinedIcon
              sx={{
                color: (theme) => theme.palette.brand.cyan,
                fontSize: 28,
              }}
            />
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
            >
              Click to upload main image
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.muted" }}>
              JPG, PNG | Max {MAX_FILE_MB}MB
            </Typography>
            {(mainImage || draft._editMeta?.mainImageUrl) && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: (theme) => theme.palette.brand.cyanDark,
                  fontWeight: 600,
                }}
              >
                {mainImage?.name || "Current main image on file"}
              </Typography>
            )}
          </Stack>
        </Paper>
        {errors.mainImage && (
          <FormHelperText error sx={{ mt: 0.5 }}>{errors.mainImage}</FormHelperText>
        )}

        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 13,
            color: "text.primary",
            mt: 2.5,
            mb: 1,
          }}
        >
          Gallery Images (Up to {MAX_GALLERY_FILES})
        </Typography>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          multiple
          onChange={handleGalleryUpload}
          hidden
        />
        <Paper
          component="button"
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          elevation={0}
          sx={{
            width: "100%",
            minHeight: 96,
            borderRadius: 2,
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 2,
            cursor: "pointer",
            "&:hover": {
              borderColor: (theme) => theme.palette.text.faint,
              bgcolor: "background.subtle",
            },
            ...filledUploadDropzoneSx(
              theme,
              galleryImages.length > 0,
              theme.palette.border.medium
            ),
          }}
        >
          <Stack alignItems="center" spacing={0.5}>
            <UploadFileOutlinedIcon sx={{ color: theme.palette.brand.cyan, fontSize: 26 }} />
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
            >
              Click to upload gallery images
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.faint" }}>
              Select multiple images
            </Typography>
            {!!galleryImages.length && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: (theme) => theme.palette.brand.cyanDark,
                  fontWeight: 600,
                }}
              >
                {galleryImages.length} image{galleryImages.length > 1 ? "s" : ""} selected
              </Typography>
            )}
          </Stack>
        </Paper>
        {errors.galleryImages && (
          <FormHelperText error sx={{ mt: 0.5 }}>{errors.galleryImages}</FormHelperText>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.medium,
              color: "text.neutral",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: (theme) => theme.palette.text.faint,
                bgcolor: "background.subtle",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
